import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box, Typography, Button, Grid, IconButton,
    Card, Chip, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, MenuItem, InputAdornment, Dialog,
    DialogContent, DialogActions, CircularProgress, Avatar, Tooltip, Paper,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import SavingsIcon from '@mui/icons-material/Savings';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { useSelector } from 'react-redux';
import { selectWebsiteSettings } from '../../../redux/slices/websiteSettingsSlice';
import { selectRoles } from '../../../redux/slices/rolesSlice';
import * as XLSX from 'xlsx';
import http, { apiErrorMessage } from '../../../Api/http';
import SnackBar from '../../SnackBar';
import { TableRowsSkeleton } from '../../ContentLoader';
import { getPayrollRegister, getPayrollCycle, postLockAttendancePayrollCycle, postCalculatePayrollCycle } from '../../../Api/Api';
import PayrollCycleStepper, { PAYROLL_STAGES } from './PayrollCycleStepper';
import PayrollInputsDialog from './PayrollInputsDialog';
import { useCrumbActionsSetter } from '../../../utils/crumbActions';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';

const buildMonths = () => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        opts.push({
            value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
            label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
            isCurrent: i === 0,
        });
    }
    return opts;
};


const currentCycleKey = () => {
    const d = new Date();
    return `payroll-cycle-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const PRIMARY_DARK = '#0E4A75';
const PRIMARY_BORDER = '#B3D3E8';

const AVATAR_PALETTE = ['#0E7490', '#135F93', '#C2410C', '#0E4A75', '#1D4ED8', '#BE185D', '#A16207', '#0F766E'];
const avatarColorFor = (name = '') => {
    const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
    return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
};

const getInitials = (name = '') =>
    name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function SalaryRegister() {
    const websiteSettings = useSelector(selectWebsiteSettings);
    const roles = useSelector(selectRoles);
    const user = useSelector((state) => state.auth);
    const userType = user?.userType;
    const canApprove = userType === 'superadmin' || userType === 'admin';

    // Stages: 0 Attendance, 1 Inputs, 2 Calculation, 3 Approval, 4 Credited, 5 complete.
    const [storedStage, setStoredStage] = useState(() => {
        try {
            const stored = localStorage.getItem(currentCycleKey());
            return stored ? Number(stored) : 3;
        } catch {
            return 3;
        }
    });

    const [cycleStatus, setCycleStatus] = useState('');
    const [inputsOpen, setInputsOpen] = useState(false);
    const STATUS_STAGE = { none: 0, attendancelocked: 1, calculated: 3, approved: 4, credited: 5, paid: 5 };
    const statusKey = String(cycleStatus).toLowerCase();
    // The cycle's real status wins over the locally stored stage.
    const currentStage = statusKey in STATUS_STAGE ? STATUS_STAGE[statusKey] : storedStage;

    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [creditDialogOpen, setCreditDialogOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const months = useMemo(buildMonths, []);
    const [month, setMonth] = useState(months[0].value);
    const [isRunning, setIsRunning] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRole, setSelectedRole] = useState('All');
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const [stats, setStats] = useState({
        totalGrossSalary: 0,
        totalNetSalary: 0,
        totalDeductions: 0,
        totalEmployees: 0,
    });
    const [records, setRecords] = useState([]);

    const [snackOpen, setSnackOpen] = useState(false);
    const [snackStatus, setSnackStatus] = useState(false);
    const [snackColor, setSnackColor] = useState(false);
    const [snackMessage, setSnackMessage] = useState('');
    const showSnack = (msg, success) => {
        setSnackMessage(msg); setSnackOpen(true); setSnackColor(success); setSnackStatus(success);
    };

    const fetchSalaryRegister = useCallback(async () => {
        setIsLoading(true);
        setRecords([]);
        setStats({ totalGrossSalary: 0, totalNetSalary: 0, totalDeductions: 0, totalEmployees: 0 });
        http.get(getPayrollCycle, { params: { payoutMonth: month } })
            .then((cyc) => setCycleStatus(cyc?.data?.error ? '' : (cyc?.data?.data?.status || '')))
            .catch(() => setCycleStatus(''));
        try {
            const res = await http.get(getPayrollRegister, { params: { payoutMonth: month } });
            if (res.data && !res.data.error) {
                const d = res.data.data || {};
                setStats({
                    totalGrossSalary: d.totalGross ?? 0,
                    totalNetSalary: d.totalNet ?? 0,
                    totalDeductions: d.totalDeductions ?? 0,
                    totalEmployees: d.count ?? (d.rows?.length || 0),
                });
                setRecords((d.rows || []).map(r => ({
                    ...r,
                    rollNumber: r.employeeCode ?? r.rollNumber ?? '',
                })));
            } else {
                setRecords([]);
                setStats({ totalGrossSalary: 0, totalNetSalary: 0, totalDeductions: 0, totalEmployees: 0 });
            }
        } catch (err) {
            setRecords([]);
            setStats({ totalGrossSalary: 0, totalNetSalary: 0, totalDeductions: 0, totalEmployees: 0 });
            if (err?.response?.status !== 404) showSnack('Failed to load salary register', false);
        } finally {
            setIsLoading(false);
        }
    }, [month]);

    useEffect(() => {
        fetchSalaryRegister();
    }, [fetchSalaryRegister]);

    const handleRunPayroll = async () => {
        setIsRunning(true);
        try {
            const body = { payoutMonth: month };
            if (statusKey !== 'attendancelocked') {
                const lock = await http.post(postLockAttendancePayrollCycle, body);
                if (lock.data?.error) { showSnack(lock.data.message || 'Failed to lock attendance', false); return; }
                setCycleStatus('AttendanceLocked');
            }
            // Attendance is frozen; HR now fills in the Inputs grid before calculating.
            setInputsOpen(true);
        } catch (err) {
            showSnack(apiErrorMessage(err, 'Failed to lock attendance. Please try again.'), false);
        } finally {
            setIsRunning(false);
        }
    };

    const handleCalculate = async () => {
        setIsRunning(true);
        try {
            const calc = await http.post(postCalculatePayrollCycle, { payoutMonth: month });
            if (calc.data?.error) { showSnack(calc.data.message || 'Failed to calculate payroll', false); return; }
            setInputsOpen(false);
            showSnack('Payroll calculated — register updated. Approve it from the Approve & Credit tab.', true);
            await fetchSalaryRegister();
        } catch (err) {
            showSnack(apiErrorMessage(err, 'Failed to calculate payroll. Please try again.'), false);
        } finally {
            setIsRunning(false);
        }
    };

    const roleOptions = useMemo(
        () => ['All', ...roles.map(r => r.name).filter(Boolean)],
        [roles],
    );

    const filteredData = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        const wantRole = selectedRole.trim().toLowerCase();
        return records.filter(emp => {
            const matchesSearch = !q
                || (emp.name || '').toLowerCase().includes(q)
                || (emp.rollNumber || '').toLowerCase().includes(q);
            const empRole = (emp.role || emp.loginRole || emp.designation || '').toLowerCase();
            const matchesRole = selectedRole === 'All' || empRole === wantRole;
            return matchesSearch && matchesRole;
        });
    }, [records, searchTerm, selectedRole]);

    const searchActive = searchTerm.trim().length > 0;
    const roleActive = selectedRole !== 'All';
    const anyFilterActive = searchActive || roleActive;

    const visibleTotals = useMemo(() => filteredData.reduce((a, r) => ({
        gross: a.gross + (Number(r.grossSalary) || 0),
        deductions: a.deductions + (Number(r.deductions) || 0),
        net: a.net + (Number(r.netSalary) || 0),
    }), { gross: 0, deductions: 0, net: 0 }), [filteredData]);

    const handleViewDetails = (employee) => {
        setSelectedEmployee(employee);
        setOpenDialog(true);
    };

    const handlePrintPayslip = () => {
        const e = selectedEmployee;
        if (!e) return;
        const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
        const money = (v) => esc(formatINR(v));
        const html = `<!doctype html><html><head><meta charset="utf-8"><title>Payslip — ${esc(e.name)}</title>
<style>
  *{box-sizing:border-box;}
  body{font-family:Arial,Helvetica,sans-serif;color:#111827;margin:0;padding:28px;}
  .wrap{max-width:640px;margin:0 auto;}
  .head{text-align:center;border-bottom:3px solid #135F93;padding-bottom:14px;margin-bottom:20px;}
  .brand{font-size:22px;font-weight:800;color:#135F93;letter-spacing:.5px;}
  .slip{font-size:15px;font-weight:700;margin-top:4px;letter-spacing:3px;color:#111827;}
  .meta{display:flex;justify-content:space-between;font-size:12px;color:#64748B;margin-top:10px;}
  .card{border:1px solid #E5E7EB;border-radius:8px;padding:16px;margin-bottom:16px;}
  .sec{font-size:11px;font-weight:800;color:#374151;text-transform:uppercase;letter-spacing:.6px;margin-bottom:12px;}
  .grid{display:flex;flex-wrap:wrap;}
  .grid>div{width:50%;margin-bottom:10px;}
  .label{font-size:10px;color:#9CA3AF;font-weight:700;text-transform:uppercase;letter-spacing:.4px;}
  .val{font-size:13px;font-weight:700;color:#111827;margin-top:2px;text-transform:capitalize;}
  .row{display:flex;justify-content:space-between;align-items:center;padding:9px 0;border-bottom:1px dashed #E5E7EB;}
  .rlabel{font-size:12.5px;color:#6B7280;font-weight:600;}
  .rval{font-size:13px;font-weight:700;}
  .pdate{display:flex;justify-content:space-between;align-items:center;padding-top:11px;margin-top:4px;border-top:1px solid #E5E7EB;}
  .net{border:1px solid #B3D3E8;border-radius:8px;padding:16px;background:#F3F9FD;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px;}
  .netlabel{font-size:11px;font-weight:800;color:#0E4A75;text-transform:uppercase;letter-spacing:.6px;}
  .netval{font-size:26px;font-weight:800;color:#0E4A75;margin-top:4px;line-height:1.1;}
  .netsub{font-size:10.5px;color:#4B5563;margin-top:4px;}
  .chip{background:#135F93;color:#fff;font-weight:700;font-size:11px;padding:6px 14px;border-radius:20px;}
  @media print{body{padding:14px;}}
</style></head><body><div class="wrap">
  <div class="head">
    <div class="brand">ARA HUMANSYNC</div>
    <div class="slip">SALARY SLIP</div>
    <div class="meta"><span>${esc(e.name)} · ${esc(e.rollNumber)}</span><span>Payment Date: ${esc(e.paymentDate || '—')}</span></div>
  </div>
  <div class="card">
    <div class="sec">Employee</div>
    <div class="grid">
      <div><div class="label">Employee Name</div><div class="val">${esc(e.name || '—')}</div></div>
      <div><div class="label">Roll Number</div><div class="val" style="text-transform:none">${esc(e.rollNumber || '—')}</div></div>
      <div><div class="label">Designation</div><div class="val">${esc(e.designation || '—')}</div></div>
      <div><div class="label">Department</div><div class="val">${esc(e.department || '—')}</div></div>
    </div>
  </div>
  <div class="card">
    <div class="sec">Salary Breakdown</div>
    <div class="row"><span class="rlabel">Basic Salary</span><span class="rval" style="color:#111827">${money(e.basicSalary)}</span></div>
    <div class="row"><span class="rlabel">Gross Salary</span><span class="rval" style="color:#0E4A75">${money(e.grossSalary)}</span></div>
    <div class="row" style="border-bottom:none"><span class="rlabel">Total Deductions</span><span class="rval" style="color:#DC2626">${money(e.deductions)}</span></div>
    <div class="pdate"><span class="rlabel" style="color:#9CA3AF">Payment Date</span><span class="rval" style="color:#374151;font-weight:600">${esc(e.paymentDate || '—')}</span></div>
  </div>
  <div class="net">
    <div>
      <div class="netlabel">Net Salary (Take Home)</div>
      <div class="netval">${money(e.netSalary)}</div>
      <div class="netsub">Disbursed to registered bank account</div>
    </div>
    <div class="chip">Bank Transfer</div>
  </div>
</div></body></html>`;

        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        document.body.appendChild(iframe);
        const doc = iframe.contentWindow?.document;
        if (!doc) { document.body.removeChild(iframe); return; }
        doc.open();
        doc.write(html);
        doc.close();
        const w = iframe.contentWindow;
        w.focus();
        setTimeout(() => {
            w.print();
            setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 1000);
        }, 120);
    };

    const handleExportRegister = () => {
        const excelData = filteredData.map((emp, index) => ({
            'S.No': index + 1,
            'Roll Number': emp.rollNumber,
            'Employee': emp.name,
            'Designation': emp.designation,
            'Department': emp.department,
            'Pay Type': emp.payType,
            'Monthly Gross': emp.monthlyGross,
            'Reporting Days': emp.reportingDays,
            'OT Days': emp.otDays,
            'Earned Gross': emp.earnedGross,
            'Basic Earned': emp.basicSalary,
            'Gross Salary': emp.grossSalary,
            'Deductions': emp.deductions,
            'Net Salary': emp.netSalary,
            'Payment Date': emp.paymentDate,
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [
            { wch: 8 }, { wch: 12 }, { wch: 20 }, { wch: 18 }, { wch: 15 },
            { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 14 },
            { wch: 14 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
        ];
        XLSX.utils.book_append_sheet(wb, ws, 'Salary Register');
        const fileName = `Salary_Register_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
        showSnack('Salary register exported successfully!', true);
    };

    const persistStage = (stage) => {
        setStoredStage(stage);
        try {
            localStorage.setItem(currentCycleKey(), String(stage));
        } catch {
        }
    };

    const handleApproveRegister = async () => {
        setActionLoading(true);
        await new Promise(r => setTimeout(r, 400));
        persistStage(4);
        setActionLoading(false);
        setApproveDialogOpen(false);
        showSnack('Salary register approved — ready for disbursement', true);
    };

    const handleMarkCredited = async () => {
        setActionLoading(true);
        await new Promise(r => setTimeout(r, 400));
        persistStage(5);
        setActionLoading(false);
        setCreditDialogOpen(false);
        showSnack('Salaries marked as credited — payslips can be shared', true);
    };

    const stageMeta = currentStage >= PAYROLL_STAGES.length
        ? { label: 'Cycle Complete', tone: '#16A34A', bg: '#DCFCE7', border: '#BBF7D0' }
        : { label: PAYROLL_STAGES[Math.max(0, Math.min(currentStage, PAYROLL_STAGES.length - 1))].label, tone: '#0E4A75', bg: '#E4F0F9', border: '#B3D3E8' };

    const cycleMonthLabel = new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });

    const alreadyRun = ['calculated', 'approved', 'credited', 'paid'].includes(String(cycleStatus).toLowerCase());

    // Month, Run Payroll / Inputs, role + search filters and Export sit at the right end of the breadcrumb row.
    const setCrumbActions = useCrumbActionsSetter();
    const actionsRef = useRef({});
    actionsRef.current = { handleRunPayroll, handleExportRegister };
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        const pill = { height: 42, fontSize: 12.5, borderRadius: '7px', bgcolor: '#fff' };
        setCrumbActions(
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    select size="small" value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 180 }, '& .MuiOutlinedInput-root': pill }}
                >
                    {months.map(m => <MenuItem key={m.value} value={m.value} sx={{ fontSize: 13 }}>{m.label}{m.isCurrent ? '  (current)' : ''}</MenuItem>)}
                </TextField>
                <TextField
                    select size="small" value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    slotProps={{ input: { startAdornment: <InputAdornment position="start"><BadgeOutlinedIcon sx={{ fontSize: 16, color: roleActive ? PRIMARY : '#9CA3AF' }} /></InputAdornment> } }}
                    sx={{ width: { xs: '100%', sm: 170 }, '& .MuiOutlinedInput-root': { ...pill, bgcolor: roleActive ? PRIMARY_LIGHT : '#fff', '& fieldset': { borderColor: roleActive ? PRIMARY_BORDER : '#E5E7EB' } } }}
                >
                    {roleOptions.map(role => <MenuItem key={role} value={role} sx={{ fontSize: '12.5px' }}>{role === 'All' ? 'All Roles' : role}</MenuItem>)}
                </TextField>
                <TextField
                    size="small" placeholder="Search by name or roll no..." value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: searchActive ? PRIMARY : '#9CA3AF' }} /></InputAdornment>,
                            endAdornment: searchActive ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ p: 0.3 }}><CloseIcon sx={{ fontSize: 14, color: '#6B7280' }} /></IconButton>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    sx={{ width: { xs: '100%', sm: 240 }, '& .MuiOutlinedInput-root': pill }}
                />
                <Button
                    variant="contained" disableElevation
                    startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                    onClick={() => actionsRef.current.handleExportRegister()}
                    sx={{
                        textTransform: 'none', bgcolor: PRIMARY_LIGHT, color: PRIMARY, borderRadius: '7px',
                        fontSize: '12.5px', fontWeight: 700, px: 2.4, height: 42, boxShadow: 'none',
                        border: `1.5px solid ${PRIMARY_BORDER}`,
                        '&:hover': { bgcolor: '#D0E5F3', borderColor: PRIMARY, boxShadow: 'none' },
                    }}
                >
                    Export Excel
                </Button>
                {!alreadyRun && (
                    <Button
                        variant="contained" disableElevation
                        onClick={() => actionsRef.current.handleRunPayroll()}
                        disabled={isRunning || isLoading}
                        startIcon={isRunning ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />}
                        sx={{
                            textTransform: 'none', fontSize: '12.5px', fontWeight: 700,
                            bgcolor: PRIMARY, color: '#fff', border: `1.5px solid ${PRIMARY}`,
                            borderRadius: '7px', px: 2.2, height: 42, boxShadow: 'none',
                            '&:hover': { bgcolor: PRIMARY_DARK, borderColor: PRIMARY_DARK },
                            '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF', borderColor: '#E5E7EB' },
                        }}
                    >
                        {isRunning ? 'Working…' : statusKey === 'attendancelocked' ? 'Enter Inputs & Calculate' : 'Run Payroll'}
                    </Button>
                )}
                {alreadyRun && (
                    <Button
                        disableElevation
                        onClick={() => setInputsOpen(true)}
                        startIcon={<EditNoteRoundedIcon sx={{ fontSize: 17 }} />}
                        sx={{
                            textTransform: 'none', fontSize: '12.5px', fontWeight: 700,
                            color: PRIMARY_DARK, bgcolor: PRIMARY_LIGHT, border: `1.5px solid ${PRIMARY_BORDER}`,
                            borderRadius: '7px', px: 2, height: 42,
                            '&:hover': { bgcolor: '#D0E5F3', borderColor: PRIMARY },
                        }}
                    >
                        View Inputs
                    </Button>
                )}
                {alreadyRun && (
                    <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: '17px !important' }} />}
                        label={`Payroll ${String(cycleStatus).toLowerCase()}`}
                        sx={{ height: 42, borderRadius: '7px', px: 0.6, fontSize: 12.5, fontWeight: 700, bgcolor: '#ECFDF3', color: '#027A48', textTransform: 'capitalize', '& .MuiChip-icon': { color: '#027A48' } }}
                    />
                )}
            </Box>,
        );
        return () => setCrumbActions(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, month, months, selectedRole, roleOptions, roleActive, searchTerm, searchActive, alreadyRun, cycleStatus, statusKey, isRunning, isLoading]);

    return (
        <>
            <SnackBar open={snackOpen} color={snackColor} setOpen={setSnackOpen} status={snackStatus} message={snackMessage} />

            <style>
                {`
                    @page { size: A4; margin: 15mm; }
                    @media print {
                        body * { visibility: hidden; }
                        #payslip-print-content, #payslip-print-content * { visibility: visible; }
                        #payslip-print-content { position: absolute; left: 0; top: 0; width: 100%; padding: 10px; }
                        .print-hide { display: none !important; }
                        .MuiDialog-paper { box-shadow: none !important; margin: 0 !important; max-width: 100% !important; }
                        .print-no-break { page-break-inside: avoid !important; break-inside: avoid !important; }
                        .MuiCard-root { margin-bottom: 8px !important; page-break-inside: avoid !important; }
                        .MuiCardContent-root { padding: 12px !important; }
                        #payslip-print-content { transform: scale(0.95); transform-origin: top left; }
                    }
                `}
            </style>

            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '88vh',
                p: 2,
            }}>
                {!setCrumbActions && (
                <Box sx={{
                    pb: 2.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box>
                            <Typography sx={{ fontSize: '19px', fontWeight: 800, color: '#111827', letterSpacing: '-0.3px' }}>
                                Payroll Register
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.2 }}>
                                Calculated gross, deductions &amp; net pay for every employee this cycle
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            select size="small" value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            sx={{ width: { xs: '100%', sm: 180 }, '& .MuiOutlinedInput-root': { height: 34, fontSize: 12.5, borderRadius: '7px', bgcolor: '#fff' } }}
                        >
                            {months.map(m => <MenuItem key={m.value} value={m.value} sx={{ fontSize: 13 }}>{m.label}{m.isCurrent ? '  (current)' : ''}</MenuItem>)}
                        </TextField>
                        {!alreadyRun && (
                            <Button
                                variant="contained" disableElevation
                                onClick={handleRunPayroll}
                                disabled={isRunning || isLoading}
                                startIcon={isRunning ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />}
                                sx={{
                                    textTransform: 'none', fontSize: '12.5px', fontWeight: 700,
                                    bgcolor: PRIMARY, color: '#fff', border: `1.5px solid ${PRIMARY}`,
                                    borderRadius: '30px', px: 2.2, height: 34,
                                    boxShadow: `0 2px 6px ${PRIMARY}40`,
                                    '&:hover': { bgcolor: PRIMARY_DARK, borderColor: PRIMARY_DARK },
                                    '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF', borderColor: '#E5E7EB' },
                                }}
                            >
                                {isRunning ? 'Working…' : statusKey === 'attendancelocked' ? 'Enter Inputs & Calculate' : 'Run Payroll'}
                            </Button>
                        )}
                        {alreadyRun && (
                            <Button
                                disableElevation
                                onClick={() => setInputsOpen(true)}
                                startIcon={<EditNoteRoundedIcon sx={{ fontSize: 17 }} />}
                                sx={{
                                    textTransform: 'none', fontSize: '12.5px', fontWeight: 700,
                                    color: PRIMARY_DARK, bgcolor: PRIMARY_LIGHT, border: `1.5px solid ${PRIMARY_BORDER}`,
                                    borderRadius: '30px', px: 2, height: 34,
                                    '&:hover': { bgcolor: '#D0E5F3', borderColor: PRIMARY },
                                }}
                            >
                                View Inputs
                            </Button>
                        )}
                        {alreadyRun && (
                            <Chip
                                icon={<CheckCircleIcon sx={{ fontSize: '17px !important' }} />}
                                label={`Payroll ${String(cycleStatus).toLowerCase() === 'calculated' ? 'calculated' : String(cycleStatus).toLowerCase()}`}
                                sx={{ height: 34, borderRadius: '30px', px: 0.6, fontSize: 12.5, fontWeight: 700, bgcolor: '#DCFCE7', color: '#16A34A', textTransform: 'capitalize', '& .MuiChip-icon': { color: '#16A34A' } }}
                            />
                        )}
                        <TextField
                            select
                            size="small"
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <BadgeOutlinedIcon sx={{ fontSize: 16, color: roleActive ? PRIMARY : '#9CA3AF' }} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        padding: '0 12px',
                                        borderRadius: '50px',
                                        height: '32px',
                                        fontSize: '12px',
                                    },
                                },
                            }}
                            sx={{
                                width: { xs: '100%', sm: 190 },
                                '& .MuiOutlinedInput-root': {
                                    minHeight: '32px',
                                    bgcolor: roleActive ? PRIMARY_LIGHT : '#fff',
                                    '& fieldset': { borderColor: roleActive ? PRIMARY_BORDER : '#E5E7EB' },
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: websiteSettings.mainColor,
                                },
                            }}
                        >
                            {roleOptions.map(role => (
                                <MenuItem key={role} value={role} sx={{ fontSize: '12.5px' }}>
                                    {role === 'All' ? 'All Roles' : role}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search by name or roll no..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 18, color: searchActive ? PRIMARY : '#9CA3AF' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchActive ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ p: 0.3 }}>
                                                <CloseIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                    sx: {
                                        padding: '0 12px',
                                        borderRadius: '50px',
                                        height: '32px',
                                        fontSize: '12px',
                                    },
                                },
                            }}
                            sx={{
                                width: { xs: '100%', sm: 240 },
                                '& .MuiOutlinedInput-root': {
                                    minHeight: '32px',
                                    paddingRight: '3px',
                                    backgroundColor: '#fff',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: websiteSettings.mainColor,
                                },
                            }}
                        />

                        <Button
                            variant="contained"
                            disableElevation
                            startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                            onClick={handleExportRegister}
                            sx={{
                                textTransform: 'none',
                                bgcolor: '#E4F0F9',
                                color: '#135F93',
                                borderRadius: '30px',
                                fontSize: '12.5px',
                                fontWeight: 700,
                                px: 2.4, height: 34,
                                boxShadow: 'none',
                                border: '1.5px solid #B3D3E8',
                                '&:hover': {
                                    bgcolor: '#D0E5F3',
                                    borderColor: '#B3D3E8',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            Export Excel
                        </Button>
                    </Box>
                </Box>
                )}

                <Box sx={{ flex: 1 }}>

                    <Paper elevation={0} sx={{
                        p: 1.6, mb: 2,
                        borderRadius: '12px',
                        border: '1px solid #E1F0FB',
                        background: 'linear-gradient(135deg, #F3F9FD 0%, #F3F8FC 55%, #E4F0F9 100%)',
                        boxShadow: '0 1px 3px rgba(19,95,147,0.06)',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2.2,
                            flexWrap: 'wrap',
                            gap: 1,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                <Box sx={{
                                    width: 38, height: 38, borderRadius: '10px', flexShrink: 0,
                                    bgcolor: '#fff', border: '1px solid #E1F0FB',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 1px 3px rgba(19,95,147,0.12)',
                                }}>
                                    <AssignmentIcon sx={{ fontSize: 20, color: PRIMARY }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#111827', letterSpacing: '-0.2px' }}>
                                        Payroll Cycle — {cycleMonthLabel}
                                    </Typography>
                                    <Typography sx={{ fontSize: '11.5px', color: '#6B7280' }}>
                                        Track the processing stage of this month's payroll
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Chip
                                    icon={currentStage >= PAYROLL_STAGES.length
                                        ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                                        : <PendingActionsIcon sx={{ fontSize: '14px !important' }} />}
                                    label={currentStage >= PAYROLL_STAGES.length
                                        ? 'Payroll Cycle Complete'
                                        : `Currently at: ${stageMeta.label}`}
                                    sx={{
                                        bgcolor: stageMeta.bg,
                                        color: stageMeta.tone,
                                        border: `1px solid ${stageMeta.border}`,
                                        fontWeight: 700, fontSize: '11px', height: 26,
                                        '& .MuiChip-icon': { color: stageMeta.tone },
                                    }}
                                />
                                {canApprove && currentStage === 3 && (
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<HowToRegIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => setApproveDialogOpen(true)}
                                        sx={{
                                            textTransform: 'none', fontSize: '12.5px', fontWeight: 700,
                                            bgcolor: '#E4F0F9', color: '#135F93',
                                            borderRadius: '30px',
                                            px: 2.2, height: 32,
                                            border: '1.5px solid #B3D3E8',
                                            '&:hover': { bgcolor: '#D0E5F3', borderColor: '#B3D3E8' },
                                        }}
                                    >
                                        Approve Register
                                    </Button>
                                )}
                                {canApprove && currentStage === 4 && (
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<SavingsIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => setCreditDialogOpen(true)}
                                        sx={{
                                            textTransform: 'none', fontSize: '12.5px', fontWeight: 700,
                                            bgcolor: PRIMARY, color: '#fff',
                                            borderRadius: '30px',
                                            px: 2.2, height: 32,
                                            border: `1.5px solid ${PRIMARY}`,
                                            '&:hover': { bgcolor: PRIMARY_DARK, borderColor: PRIMARY_DARK },
                                        }}
                                    >
                                        Mark as Salary Credited
                                    </Button>
                                )}
                                {!canApprove && (
                                    <Chip
                                        icon={<LockOutlinedIcon sx={{ fontSize: '12px !important' }} />}
                                        label="Read-only access"
                                        size="small"
                                        sx={{
                                            bgcolor: '#F3F4F6', color: '#6B7280',
                                            border: '1px solid #E5E7EB',
                                            fontWeight: 600, fontSize: '10.5px', height: 24,
                                            '& .MuiChip-icon': { color: '#6B7280', ml: '6px' },
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        <PayrollCycleStepper
                            stages={PAYROLL_STAGES}
                            current={currentStage}
                            tone={stageMeta.tone}
                            toneBg={stageMeta.bg}
                        />
                    </Paper>

                    <Card sx={{ border: '1px solid #E1EAF1', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.05)', bgcolor: '#fff', overflow: 'hidden' }}>
                        <TableContainer sx={{ overflowX: 'hidden' }}>
                            <Table stickyHeader size="small" sx={{ width: '100%' }}>
                                <TableHead>
                                    <TableRow>
                                        {[
                                            'S.No', 'Employee', 'Department', 'Basic Salary', 'Gross Salary',
                                            'Deductions', 'Net Salary', 'Payment Date', 'Actions',
                                        ].map((header) => (
                                            <TableCell
                                                key={header}
                                                sx={{
                                                    fontWeight: 700, fontSize: '10.5px',
                                                    color: '#5B7A94',
                                                    bgcolor: '#F3F8FC',
                                                    textTransform: 'uppercase', letterSpacing: 0.7,
                                                    whiteSpace: 'nowrap', py: 1.1,
                                                    borderBottom: '1px solid #E1EAF1',
                                                }}
                                            >
                                                {header}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRowsSkeleton rows={7} cols={9} avatarCol={1} />
                                    ) : filteredData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                                                <Box sx={{
                                                    width: 56, height: 56, borderRadius: '50%',
                                                    bgcolor: '#F3F4F6', mx: 'auto', mb: 1.2,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <DescriptionIcon sx={{ fontSize: 28, color: '#9CA3AF' }} />
                                                </Box>
                                                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>
                                                    {anyFilterActive ? 'No matching salary records' : 'No salary records yet'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '11.5px', color: '#9CA3AF', mt: 0.4 }}>
                                                    {anyFilterActive ? 'Try adjusting filters or search term' : 'Records will appear here once payroll is processed'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredData.map((row, idx) => {
                                        const avColor = avatarColorFor(row.name || '');
                                        return (
                                            <TableRow
                                                key={row.id}
                                                sx={{
                                                    bgcolor: idx % 2 ? '#F3F8FC' : '#fff',
                                                    '&:hover': { bgcolor: '#F3F8FC' },
                                                    transition: 'background-color 0.15s',
                                                }}
                                            >
                                                <TableCell sx={{ width: 50, borderBottom: '1px solid #EEF0F6', py: 1.1 }}>
                                                    <Typography sx={{ fontSize: '12px', color: '#9CA3AF', fontWeight: 500 }}>
                                                        {idx + 1}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                                        <Avatar sx={{
                                                            width: 32, height: 32,
                                                            bgcolor: `${avColor}15`,
                                                            color: avColor,
                                                            fontSize: '11px', fontWeight: 700,
                                                            border: `1px solid ${avColor}33`,
                                                        }}>
                                                            {getInitials(row.name || '?')}
                                                        </Avatar>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{
                                                                fontSize: '13px', fontWeight: 600,
                                                                color: '#111827', whiteSpace: 'nowrap',
                                                            }}>
                                                                {row.name || '—'}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 500 }}>
                                                                {row.rollNumber}{row.designation ? ` · ${row.designation}` : ''}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.1 }}>
                                                    {row.department ? (
                                                        <Chip
                                                            label={row.department}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#F3F4F6', color: '#374151',
                                                                fontWeight: 600, fontSize: '10.5px', height: 22,
                                                                border: '1px solid #E5E7EB',
                                                                textTransform: 'capitalize',
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography sx={{ fontSize: '12px', color: '#CBD5E1' }}>—</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.1 }}>
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>
                                                        {formatINR(row.basicSalary)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.1 }}>
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#0E4A75' }}>
                                                        {formatINR(row.grossSalary)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.1 }}>
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#DC2626' }}>
                                                        {formatINR(row.deductions)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.1 }}>
                                                    <Box sx={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                                        px: 1.1, py: 0.5, borderRadius: '7px',
                                                        bgcolor: '#EDF6FD', border: '1px solid #C2DCEE',
                                                    }}>
                                                        <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#0E4A75' }}>
                                                            {formatINR(row.netSalary)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.1 }}>
                                                    <Typography sx={{ fontSize: '12.5px', color: '#374151', fontWeight: 500 }}>
                                                        {row.paymentDate || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #EEF0F6', py: 1.1 }}>
                                                    <Tooltip arrow title="View payslip">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewDetails(row)}
                                                            sx={{
                                                                bgcolor: '#E4F0F9', borderRadius: '7px',
                                                                border: '1px solid #B3D3E8',
                                                                '&:hover': { bgcolor: '#D0E5F3' },
                                                            }}
                                                        >
                                                            <VisibilityIcon sx={{ fontSize: 14, color: '#0E4A75' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {!isLoading && filteredData.length > 0 && (
                            <Box sx={{
                                px: 2, py: 1.1, borderTop: '1px solid #CFE0EC', bgcolor: '#F3F8FC',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                flexWrap: 'wrap', gap: 1.5,
                            }}>
                                <Typography sx={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>
                                    Showing <Box component="span" sx={{ color: '#0E4A75', fontWeight: 800 }}>{filteredData.length}</Box> of {records.length} records
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.6, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                                        <Typography sx={{ fontSize: '11px', color: '#0E4A75', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Gross</Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#0E4A75' }}>{formatINR(visibleTotals.gross)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                                        <Typography sx={{ fontSize: '11px', color: '#DC2626', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Deductions</Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#DC2626' }}>{formatINR(visibleTotals.deductions)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                                        <Typography sx={{ fontSize: '11px', color: '#0E4A75', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>Net Payout</Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#0E4A75' }}>{formatINR(visibleTotals.net)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Card>
                </Box>
            </Box>

            <PayrollInputsDialog
                open={inputsOpen}
                onClose={() => setInputsOpen(false)}
                payoutMonth={month}
                monthLabel={months.find(m => m.value === month)?.label}
                onCalculate={handleCalculate}
                calculating={isRunning}
            />

            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: { sx: { borderRadius: '7px', overflow: 'hidden' } },
                }}
            >
                <Box className="print-hide" sx={{
                    px: 2.5, py: 2,
                    background: `linear-gradient(135deg, ${PRIMARY_LIGHT} 0%, #fff 70%)`,
                    borderBottom: `1px solid ${PRIMARY_BORDER}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '7px',
                            bgcolor: '#fff', border: `1px solid ${PRIMARY_BORDER}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <DescriptionIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '19px', fontWeight: 800, color: '#111827', letterSpacing: '-0.3px' }}>
                                Salary Breakdown
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.2 }}>
                                {selectedEmployee
                                    ? `${selectedEmployee.name} (${selectedEmployee.rollNumber})${selectedEmployee.designation ? ` · ${selectedEmployee.designation}` : ''}`
                                    : 'Detailed payslip view'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setOpenDialog(false)}
                        sx={{
                            width: 32, height: 32, borderRadius: '7px',
                            bgcolor: '#fff', border: '1px solid #E5E7EB',
                            '&:hover': { bgcolor: '#F9FAFB' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                    </IconButton>
                </Box>

                <DialogContent id="payslip-print-content" sx={{ p: 1.6, bgcolor: '#F9FAFB' }}>
                    {selectedEmployee && (
                        <Box>
                            <Box className="print-no-break" sx={{
                                display: 'none',
                                '@media print': { display: 'block' },
                                mb: 3, pb: 2,
                                borderBottom: `3px solid ${PRIMARY}`,
                            }}>
                                <Typography sx={{ fontSize: '19px', fontWeight: 800, textAlign: 'center', color: PRIMARY, mb: 1 }}>
                                    ARA HUMANSYNC
                                </Typography>
                                <Typography sx={{ fontSize: '20px', fontWeight: 700, textAlign: 'center', mb: 2 }}>
                                    SALARY SLIP
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                                            {selectedEmployee.name}
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#64748B' }}>
                                            {selectedEmployee.rollNumber} · {selectedEmployee.designation}
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#64748B', textTransform: 'capitalize' }}>
                                            {selectedEmployee.department}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography sx={{ fontSize: '12px', color: '#64748B' }}>
                                            Payment Date: {selectedEmployee.paymentDate}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box className="print-no-break" sx={{
                                p: 2, mb: 2,
                                bgcolor: '#fff', borderRadius: '7px',
                                border: '1px solid #E5E7EB',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <Box sx={{
                                        width: 24, height: 24, borderRadius: '7px',
                                        bgcolor: '#E4F0F9', border: '1px solid #BFDBFE',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <BadgeOutlinedIcon sx={{ fontSize: 14, color: '#0E4A75' }} />
                                    </Box>
                                    <Typography sx={{
                                        fontSize: '11px', fontWeight: 700, color: '#374151',
                                        textTransform: 'uppercase', letterSpacing: 0.6,
                                    }}>
                                        Employee
                                    </Typography>
                                </Box>

                                <Grid container spacing={1.5}>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography sx={{ fontSize: '10.5px', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                            Employee Name
                                        </Typography>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111827', mt: 0.3 }}>
                                            {selectedEmployee.name}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography sx={{ fontSize: '10.5px', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                            Roll Number
                                        </Typography>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111827', mt: 0.3 }}>
                                            {selectedEmployee.rollNumber}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography sx={{ fontSize: '10.5px', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                            Designation
                                        </Typography>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111827', mt: 0.3, textTransform: 'capitalize' }}>
                                            {selectedEmployee.designation || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography sx={{ fontSize: '10.5px', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                            Department
                                        </Typography>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111827', mt: 0.3, textTransform: 'capitalize' }}>
                                            {selectedEmployee.department || '—'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box className="print-no-break" sx={{
                                p: 2, mb: 2,
                                bgcolor: '#fff', borderRadius: '7px',
                                border: '1px solid #E5E7EB',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <Box sx={{
                                        width: 24, height: 24, borderRadius: '7px',
                                        bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <AssessmentIcon sx={{ fontSize: 14, color: PRIMARY }} />
                                    </Box>
                                    <Typography sx={{
                                        fontSize: '11px', fontWeight: 700, color: '#374151',
                                        textTransform: 'uppercase', letterSpacing: 0.6,
                                    }}>
                                        Salary Breakdown
                                    </Typography>
                                </Box>

                                {[
                                    { label: `Monthly Gross (${selectedEmployee.payType || 'Monthly'})`, value: selectedEmployee.monthlyGross, color: '#374151' },
                                    { label: `Earned Gross (${selectedEmployee.reportingDays ?? '—'} reporting days · OT ${selectedEmployee.otDays ?? 0})`, value: selectedEmployee.earnedGross, color: '#374151' },
                                    { label: 'Basic Salary (earned)', value: selectedEmployee.basicSalary, color: '#111827' },
                                    { label: 'Gross Salary', value: selectedEmployee.grossSalary, color: '#0E4A75' },
                                    { label: 'Total Deductions', value: selectedEmployee.deductions, color: '#DC2626' },
                                ].map((row, i, arr) => (
                                    <Box
                                        key={row.label}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 1.2,
                                            borderBottom: i < arr.length - 1 ? '1px dashed #E5E7EB' : 'none',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '12.5px', color: '#6B7280', fontWeight: 600 }}>
                                            {row.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: row.color }}>
                                            {formatINR(row.value)}
                                        </Typography>
                                    </Box>
                                ))}

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1.2, mt: 0.5, borderTop: '1px solid #E5E7EB' }}>
                                    <Typography sx={{ fontSize: '11.5px', color: '#9CA3AF', fontWeight: 600 }}>
                                        Payment Date
                                    </Typography>
                                    <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>
                                        {selectedEmployee.paymentDate || '—'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box className="print-no-break">
                                <Box sx={{
                                    p: 2, borderRadius: '7px',
                                    border: `1px solid ${PRIMARY_BORDER}`,
                                    background: `linear-gradient(135deg, ${PRIMARY_LIGHT} 0%, #fff 70%)`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    flexWrap: 'wrap', gap: 1.5,
                                }}>
                                    <Box>
                                        <Typography sx={{
                                            fontSize: '11px', fontWeight: 700, color: PRIMARY_DARK,
                                            textTransform: 'uppercase', letterSpacing: 0.6,
                                        }}>
                                            Net Salary (Take Home)
                                        </Typography>
                                        <Typography sx={{ fontSize: '21px', fontWeight: 800, color: PRIMARY_DARK, lineHeight: 1.1, mt: 0.4 }}>
                                            {formatINR(selectedEmployee.netSalary)}
                                        </Typography>
                                        <Typography sx={{ fontSize: '10.5px', color: '#4B5563', mt: 0.4 }}>
                                            Disbursed to registered bank account
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label="Bank Transfer"
                                        sx={{
                                            bgcolor: PRIMARY, color: '#fff',
                                            fontWeight: 700, fontSize: '11px', height: 24,
                                            borderRadius: '20px', px: 0.5,
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions className="print-hide" sx={{ px: 2.5, py: 1.8, borderTop: '1px solid #E5E7EB', bgcolor: '#fff' }}>
                    <Button
                        onClick={() => setOpenDialog(false)}
                        sx={{
                            textTransform: 'none', fontSize: '13px', fontWeight: 600,
                            color: '#374151', borderRadius: '7px',
                            border: '1px solid #E5E7EB', px: 2, height: 38,
                            '&:hover': { bgcolor: '#F9FAFB' },
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PrintIcon sx={{ fontSize: 18 }} />}
                        onClick={handlePrintPayslip}
                        sx={{
                            textTransform: 'none', fontSize: '13px', fontWeight: 700,
                            bgcolor: PRIMARY, color: '#fff', borderRadius: '7px',
                            px: 2.5, height: 38,
                            boxShadow: `0 2px 6px ${PRIMARY}33`,
                            '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: `0 4px 12px ${PRIMARY}55` },
                        }}
                    >
                        Print Payslip
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={approveDialogOpen}
                onClose={() => !actionLoading && setApproveDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                slotProps={{
                    paper: { sx: { borderRadius: '7px', overflow: 'hidden' } },
                }}
            >
                <DialogContent sx={{ pt: 3.5, pb: 2, textAlign: 'center' }}>
                    <Box sx={{
                        width: 64, height: 64, borderRadius: '50%',
                        bgcolor: '#E4F0F9', border: '4px solid #DBEAFE',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 1.8,
                    }}>
                        <HowToRegIcon sx={{ fontSize: 32, color: '#0E4A75' }} />
                    </Box>
                    <Typography sx={{ fontSize: '17px', fontWeight: 800, color: '#111827', mb: 0.8 }}>
                        Approve Salary Register?
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: '#6B7280', px: 1.5, lineHeight: 1.5 }}>
                        You're approving the salary register for{' '}
                        <strong style={{ color: '#111827' }}>{cycleMonthLabel}</strong>{' '}
                        covering <strong style={{ color: '#111827' }}>{records.length} employees</strong>
                        {' '}and a net payout of <strong style={{ color: PRIMARY_DARK }}>{formatINR(stats.totalNetSalary)}</strong>.
                        This will move the cycle to <strong>Salary Credited</strong>.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
                    <Button
                        fullWidth
                        onClick={() => setApproveDialogOpen(false)}
                        disabled={actionLoading}
                        sx={{
                            textTransform: 'none', fontSize: '13px', fontWeight: 600,
                            borderRadius: '7px', height: 38,
                            border: '1px solid #E5E7EB', color: '#374151',
                            '&:hover': { bgcolor: '#F9FAFB' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        disableElevation
                        startIcon={actionLoading
                            ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                            : <HowToRegIcon sx={{ fontSize: 16 }} />}
                        onClick={handleApproveRegister}
                        disabled={actionLoading}
                        sx={{
                            textTransform: 'none', fontSize: '13px', fontWeight: 700,
                            borderRadius: '7px', height: 38,
                            bgcolor: '#E4F0F9', color: '#135F93',
                            border: '1.5px solid #B3D3E8',
                            '&:hover': { bgcolor: '#D0E5F3', borderColor: '#B3D3E8' },
                            '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF', borderColor: '#E5E7EB' },
                        }}
                    >
                        {actionLoading ? 'Approving…' : 'Yes, Approve'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={creditDialogOpen}
                onClose={() => !actionLoading && setCreditDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                slotProps={{
                    paper: { sx: { borderRadius: '7px', overflow: 'hidden' } },
                }}
            >
                <DialogContent sx={{ pt: 3.5, pb: 2, textAlign: 'center' }}>
                    <Box sx={{
                        width: 64, height: 64, borderRadius: '50%',
                        bgcolor: PRIMARY_LIGHT, border: `4px solid ${PRIMARY_BORDER}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 1.8,
                    }}>
                        <SavingsIcon sx={{ fontSize: 32, color: PRIMARY }} />
                    </Box>
                    <Typography sx={{ fontSize: '17px', fontWeight: 800, color: '#111827', mb: 0.8 }}>
                        Mark Salary as Credited?
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: '#6B7280', px: 1.5, lineHeight: 1.5 }}>
                        Confirm that <strong style={{ color: PRIMARY_DARK }}>{formatINR(stats.totalNetSalary)}</strong>
                        {' '}has been disbursed to the registered bank accounts of{' '}
                        <strong style={{ color: '#111827' }}>{records.length} employees</strong>.
                        Once confirmed, the payroll cycle for <strong style={{ color: '#111827' }}>{cycleMonthLabel}</strong> will be marked complete.
                    </Typography>

                    <Box sx={{
                        mt: 2, p: 1.2, borderRadius: '7px',
                        bgcolor: '#FFFBEB', border: '1px solid #FDE68A',
                        display: 'flex', alignItems: 'flex-start', gap: 1, textAlign: 'left',
                    }}>
                        <WarningAmberIcon sx={{ fontSize: 16, color: '#B45309', mt: 0.1, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '11px', color: '#92400E', lineHeight: 1.4 }}>
                            This action is final for this cycle. Make sure bank transfers are confirmed before marking as credited.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
                    <Button
                        fullWidth
                        onClick={() => setCreditDialogOpen(false)}
                        disabled={actionLoading}
                        sx={{
                            textTransform: 'none', fontSize: '13px', fontWeight: 600,
                            borderRadius: '7px', height: 38,
                            border: '1px solid #E5E7EB', color: '#374151',
                            '&:hover': { bgcolor: '#F9FAFB' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        disableElevation
                        startIcon={actionLoading
                            ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                            : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                        onClick={handleMarkCredited}
                        disabled={actionLoading}
                        sx={{
                            textTransform: 'none', fontSize: '13px', fontWeight: 700,
                            borderRadius: '7px', height: 38,
                            bgcolor: PRIMARY, color: '#fff',
                            border: `1.5px solid ${PRIMARY}`,
                            '&:hover': { bgcolor: PRIMARY_DARK, borderColor: PRIMARY_DARK },
                            '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF', borderColor: '#E5E7EB' },
                        }}
                    >
                        {actionLoading ? 'Confirming…' : 'Yes, Mark Credited'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
